import { FC, SyntheticEvent, useMemo } from 'react';

import classNames from 'classnames';

import Button from '../Button';
import Typography from '../Typography';

import './Toast.scss';

import { OnChangeType, TestMetadata } from '../types';

export interface ToastProps {
  /** Title of toast */
  title?: string;
  /** Boolean to determine toast with width constraint */
  small?: boolean;
  /* Boolean to determine if the toast is showing in line with other text elements */
  inline?: boolean;
  /** Type of toast: info, warning, error, success */
  type?: 'info' | 'error' | 'success' | 'warning';
  /** Name of icon package to show (if any)*/
  iconPackage?: string;
  /** Name of Font Awesome icon to show (if any)*/
  iconTypePrimary?: string;
  /** Set role to status, alert, or alertdialog for screen readers  */
  role?: 'status' | 'alert' | 'alertdialog';
  /** Class name for alert */
  className?: string;
  /** Boolean to determine if user can dismiss toast with close button  */
  hideClose?: boolean;
  /** function to click the close button  */
  onClose?: OnChangeType;
  /** Boolean to turn off aria-live announcements */
  stopAnnouncements?: boolean;
  /** Left for backward compatibility. This will be deprecated in the next version */
  theme?: 'light' | 'dark';
  /** Button label for primary action */
  primaryButton?: string;
  /** onChange event for primary action */
  primaryButtonAction?: (e: SyntheticEvent) => void;
  /** Button label for secondary action */
  secondaryButton?: string;
  /** onChange event for secondary action */
  secondaryButtonAction?: (e: SyntheticEvent) => void;
  // Test IDs for component sections
  testMetadata?: {
    toast?: TestMetadata;
    closeButton?: TestMetadata;
  };
}

const Toast: FC<ToastProps> = ({
  title,
  small,
  inline,
  type,
  iconPackage = 'fas',
  iconTypePrimary,
  role = 'status',
  hideClose,
  stopAnnouncements,
  children,
  primaryButton,
  primaryButtonAction,
  secondaryButton,
  secondaryButtonAction,
  onClose,
  className,
  testMetadata,
}) => {
  const toastClassNames = useMemo(
    () =>
      classNames(
        'sb-toast',
        {
          'sb-toast_small': small,
          'sb-toast_inline': inline,
          'sb-toast_info': type === 'info',
          'sb-toast_error': type === 'error',
          'sb-toast_success': type === 'success',
          'sb-toast_warning': type === 'warning',
          'sb-toast_closable': !hideClose,
        },
        className,
      ),
    [small, inline, type, hideClose, className],
  );

  return (
    <section
      className={toastClassNames}
      role={role}
      aria-live={stopAnnouncements ? 'off' : 'polite'}
      {...testMetadata?.toast}
    >
      {iconTypePrimary && (
        <i
          className={classNames(
            'sb-toast__type-icon',
            iconPackage,
            iconTypePrimary,
          )}
          aria-hidden="true"
        />
      )}
      <div className="sb-toast__content">
        {title && (
          <Typography variant="T20" className="sb-toast__title">
            {title}
          </Typography>
        )}
        {children}
        {(primaryButton || secondaryButton) && (
          <div className="sb-toast__buttons">
            {secondaryButton && (
              <Button
                sizeType="smaller"
                styleType="demoted"
                colorType="primary"
                labelText={secondaryButton}
                onClick={secondaryButtonAction}
              >
                {secondaryButton}
              </Button>
            )}
            {primaryButton && (
              <Button
                className="sb-toast__button-primary"
                sizeType="smaller"
                styleType="standard"
                colorType="primary"
                labelText={primaryButton}
                onClick={primaryButtonAction}
              >
                {primaryButton}
              </Button>
            )}
          </div>
        )}
      </div>
      {!hideClose && (
        <Button
          styleType="demoted"
          iconOnly
          iconType="fa-times"
          labelText="Close"
          className="sb-toast__close-button"
          onClick={onClose}
          sizeType="smaller"
          {...testMetadata?.closeButton}
        />
      )}
    </section>
  );
};

export default Toast;
